import { DEFAULT_FALLBACK_LANG, TARGET_LANGS as REGISTRY_TARGET_LANGS, UI_LANGS as REGISTRY_UI_LANGS } from "./locale-registry.js";

/** Interface (URL prefix) languages — UI copy and chrome. */
export const UI_LANGS = REGISTRY_UI_LANGS;

/** UI langs that use a `/{lang}/…` URL prefix (all except default `de`). */
export const UI_PATH_PREFIX_LANGS = UI_LANGS.filter((l) => l !== "de");

/** Learning target language codes supported in URLs and content. */
export const TARGET_LANGS = REGISTRY_TARGET_LANGS;

/** Subset offered in the nav target-language menu and home-page CTAs. */
export const TARGET_LANGS_UI = ["de", "ch"];

/** Target language slug (what you learn) per UI language — ASCII only. */
const TARGET_SEGMENTS_BY_UI = {
  en: { de: "german", en: "english", ch: "german-switzerland" },
  de: { de: "deutsch", en: "englisch", ch: "deutsch-schweiz" },
  ar: { de: "almaniyya", en: "ingliziyya", ch: "almaniyya-swisriyya" },
  tr: { de: "almanca", en: "ingilizce", ch: "almanca-isvicre" },
  uk: { de: "nimetska", en: "angliyska", ch: "nimetska-shveitsarska" },
  ru: { de: "nemetskiy", en: "angliyskiy", ch: "nemetskiy-shveytsariya" },
  es: { de: "aleman", en: "ingles", ch: "aleman-suizo" },
  vi: { de: "tieng-duc", en: "tieng-anh", ch: "tieng-duc-thuy-si" },
  pl: { de: "niemiecki", en: "angielski", ch: "niemiecki-szwajcarski" },
  el: { de: "germanika", en: "anglika", ch: "germanika-elvetias" },
  fr: { de: "allemand", en: "anglais", ch: "allemand-suisse" },
  sl: { de: "nemscina", en: "anglescina", ch: "nemscina-svica" },
};

/** “Learn …” landing slug per UI language — ASCII only. */
const TARGET_LANDING_BY_UI = {
  en: { de: "learn-german", en: "learn-english", ch: "learn-german-switzerland" },
  de: { de: "deutsch-lernen", en: "englisch-lernen", ch: "deutsch-schweiz-lernen" },
  ar: { de: "taallam-almaniyya", en: "taallam-ingliziyya", ch: "taallam-almaniyya-swisriyya" },
  tr: { de: "almanca-ogren", en: "ingilizce-ogren", ch: "almanca-isvicre-ogren" },
  uk: { de: "vyuchay-nimetsku", en: "vyuchay-angliisku", ch: "vyuchay-nimetsku-shveitsarsku" },
  ru: { de: "uchit-nemetskiy", en: "uchit-angliyskiy", ch: "uchit-nemetskiy-shveytsariya" },
  es: { de: "aprender-aleman", en: "aprender-ingles", ch: "aprender-aleman-suizo" },
  vi: { de: "hoc-tieng-duc", en: "hoc-tieng-anh", ch: "hoc-tieng-duc-thuy-si" },
  pl: { de: "uczyc-sie-niemieckiego", en: "uczyc-sie-angielskiego", ch: "uczyc-sie-niemieckiego-szwajcarskiego" },
  el: { de: "mathaino-germanika", en: "mathaino-anglika", ch: "mathaino-germanika-elvetias" },
  fr: { de: "apprendre-allemand", en: "apprendre-anglais", ch: "apprendre-allemand-suisse" },
  sl: { de: "uciti-nemscino", en: "uciti-anglescino", ch: "uciti-nemscino-svica" },
};

/** Content section slug per UI language — ASCII only. */
const SECTION_SEGMENTS_BY_UI = {
  en: {
    adventures: "adventures",
    stories: "stories",
    dialogs: "dialogs",
    scenes: "scenes",
    vocabulary: "vocabulary",
    grammar: "grammar",
  },
  de: {
    adventures: "abenteuer",
    stories: "geschichten",
    dialogs: "dialoge",
    scenes: "szenen",
    vocabulary: "wortschatz",
    grammar: "grammatik",
  },
  ar: {
    adventures: "mughamarat",
    stories: "qusus",
    dialogs: "hiwarat",
    scenes: "mashahid",
    vocabulary: "mufradat",
    grammar: "qawaed",
  },
  tr: {
    adventures: "maceralar",
    stories: "hikayeler",
    dialogs: "diyaloglar",
    scenes: "sahneler",
    vocabulary: "kelime-hazinesi",
    grammar: "gramer",
  },
  uk: {
    adventures: "pryhody",
    stories: "istorii",
    dialogs: "dialogy",
    scenes: "stseny",
    vocabulary: "leksyka",
    grammar: "gramatyka",
  },
  ru: {
    adventures: "priklyucheniya",
    stories: "istorii",
    dialogs: "dialogi",
    scenes: "stseny",
    vocabulary: "leksika",
    grammar: "grammatika",
  },
  es: {
    adventures: "aventuras",
    stories: "historias",
    dialogs: "dialogos",
    scenes: "escenas",
    vocabulary: "vocabulario",
    grammar: "gramatica",
  },
  vi: {
    adventures: "phieu-luu",
    stories: "truyen",
    dialogs: "hoi-thoai",
    scenes: "canh",
    vocabulary: "tu-vung",
    grammar: "ngu-phap",
  },
  pl: {
    adventures: "przygody",
    stories: "historie",
    dialogs: "dialogi",
    scenes: "sceny",
    vocabulary: "slownictwo",
    grammar: "gramatyka",
  },
  el: {
    adventures: "peripeteies",
    stories: "istories",
    dialogs: "dialogoi",
    scenes: "skines",
    vocabulary: "lexilogio",
    grammar: "grammatiki",
  },
  fr: {
    adventures: "aventures",
    stories: "histoires",
    dialogs: "dialogues",
    scenes: "scenes",
    vocabulary: "vocabulaire",
    grammar: "grammaire",
  },
  sl: {
    adventures: "pustolovscine",
    stories: "zgodbe",
    dialogs: "dialogi",
    scenes: "prizori",
    vocabulary: "besedisce",
    grammar: "slovnica",
  },
};

export const TARGET_SEGMENTS = TARGET_SEGMENTS_BY_UI;
export const TARGET_LANDING_SEGMENTS = TARGET_LANDING_BY_UI;
export const SECTION_SEGMENTS = SECTION_SEGMENTS_BY_UI;

export const TARGET_SECTION_AVAILABILITY = {
  /** Order matches primary nav: Geschichten → Dialoge → Grammatik → … → Wortschatz. */
  de: ["stories", "dialogs", "grammar", "adventures", "vocabulary", "scenes"],
  en: ["adventures", "stories", "dialogs", "scenes"],
  ch: ["stories"],
};

export function isUiLang(code) {
  return UI_LANGS.includes(code);
}

function normalizeToken(value) {
  return String(value || "").trim().toLowerCase().normalize("NFC");
}

function stripDiacritics(value) {
  return normalizeToken(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/** URL segment equality: case/diacritic-insensitive. */
export function segmentTokensMatch(candidate, input) {
  const a = normalizeToken(candidate);
  const b = normalizeToken(input);
  if (!a || !b) return false;
  if (a === b) return true;
  return stripDiacritics(a) === stripDiacritics(b);
}

function uniqueMapsForUi(uiLang) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : DEFAULT_FALLBACK_LANG;
  const out = [];
  const push = (m) => {
    if (m && !out.includes(m)) out.push(m);
  };
  push(TARGET_SEGMENTS_BY_UI[safe]);
  push(TARGET_SEGMENTS_BY_UI.de);
  push(TARGET_SEGMENTS_BY_UI.en);
  push(TARGET_LANDING_BY_UI[safe]);
  push(TARGET_LANDING_BY_UI.de);
  push(TARGET_LANDING_BY_UI.en);
  return out;
}

function sectionMapsForUi(uiLang) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : DEFAULT_FALLBACK_LANG;
  const out = [];
  const push = (m) => {
    if (m && !out.includes(m)) out.push(m);
  };
  push(SECTION_SEGMENTS_BY_UI[safe]);
  push(SECTION_SEGMENTS_BY_UI.de);
  push(SECTION_SEGMENTS_BY_UI.en);
  return out;
}

export function getTargetCodeFromSegment(uiLang, segment) {
  for (const map of uniqueMapsForUi(uiLang)) {
    for (const [code, seg] of Object.entries(map)) {
      if (segmentTokensMatch(seg, segment)) return code;
    }
  }
  return null;
}

export function getSectionKeyFromSegment(uiLang, segment) {
  return parseSectionSegment(uiLang, segment).sectionKey;
}

export function parseSectionSegment(uiLang, rawSegment) {
  const segment = normalizeToken(rawSegment || "");
  const segmentFolded = stripDiacritics(segment);
  for (const map of sectionMapsForUi(uiLang)) {
    for (const [key, baseSeg] of Object.entries(map)) {
      const base = normalizeToken(baseSeg);
      const baseFolded = stripDiacritics(baseSeg);
      if (segment === base || segmentFolded === baseFolded) return { sectionKey: key, level: null };
      if (segment.startsWith(`${base}-`) || segmentFolded.startsWith(`${baseFolded}-`)) {
        const useAscii = segment.startsWith(`${base}-`);
        const cutLen = useAscii ? base.length + 1 : baseFolded.length + 1;
        const source = useAscii ? segment : segmentFolded;
        const suffix = source.slice(cutLen).trim();
        if (!suffix) return { sectionKey: key, level: null };
        return { sectionKey: key, level: suffix.toUpperCase() };
      }
    }
  }
  return { sectionKey: null, level: null };
}

export function getTargetSegment(uiLang, targetCode) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : DEFAULT_FALLBACK_LANG;
  return TARGET_SEGMENTS_BY_UI[safe]?.[targetCode] || null;
}

export function getTargetLandingSegment(uiLang, targetCode) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : DEFAULT_FALLBACK_LANG;
  return (
    TARGET_LANDING_BY_UI[safe]?.[targetCode] ||
    TARGET_SEGMENTS_BY_UI[safe]?.[targetCode] ||
    null
  );
}

export function getSectionSegment(uiLang, sectionKey) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : DEFAULT_FALLBACK_LANG;
  return SECTION_SEGMENTS_BY_UI[safe]?.[sectionKey] || null;
}

export function getAvailableSections(targetCode) {
  return TARGET_SECTION_AVAILABILITY[targetCode] || [];
}

export function isSectionAvailable(targetCode, sectionKey) {
  return getAvailableSections(targetCode).includes(sectionKey);
}

/**
 * From URL: first path segment is `en`/`ar`/…, or a German-default segment (no `de` prefix) → "de".
 */
export function getRouteUiLangFromPath(ctx) {
  const p0 = (ctx?.url?.pathname || "").split("/").filter(Boolean)[0] || "";
  /** Prefixed routes (`/vi/…`, `/en/…`) must follow the URL, not stale handler params. */
  if (isUiLang(p0)) return p0;
  const fromParam = String(ctx?.params?.uiLang || "").trim().toLowerCase();
  if (isUiLang(fromParam)) return fromParam;
  return "de";
}

/** Path prefix for non-default (German) interface languages only. */
export function pathPrefixForUiLang(uiLang) {
  const safe = UI_LANGS.includes(uiLang) ? uiLang : "de";
  return safe === "de" ? "" : `/${safe}`;
}

/**
 * Login page URL that opens the free registration panel (`view=register`).
 * @param {string} uiLang
 * @param {{ next?: string, signupOrigin?: string, signupPage?: string }} [opts]
 */
export function buildLoginRegisterHref(uiLang, opts = {}) {
  const safeUi = UI_LANGS.includes(uiLang) ? uiLang : "de";
  const q = new URLSearchParams();
  q.set("uiLang", safeUi);
  q.set("view", "register");
  const next = String(opts.next || "").trim();
  if (next) q.set("next", next);
  const signupOrigin = String(opts.signupOrigin || "").trim();
  const signupPage = String(opts.signupPage || "").trim();
  if (signupOrigin) q.set("signupOrigin", signupOrigin);
  if (signupPage) q.set("signupPage", signupPage);
  const qs = q.toString();
  // Login is an unprefixed route that reads `?uiLang`; `/<lang>/login` would 404 → home.
  return `/login${qs ? `?${qs}` : ""}`;
}

export function buildLocalizedPath({
  uiLang,
  targetCode,
  sectionKey,
  level = null,
  slug = null,
  sceneSlug = null,
}) {
  const safeUi = UI_LANGS.includes(uiLang) ? uiLang : "de";
  const targetSeg = getTargetSegment(safeUi, targetCode);
  const sectionSeg = getSectionSegment(safeUi, sectionKey);
  if (!targetSeg || !sectionSeg) return "/";
  const levelSuffix = level ? `-${String(level).toLowerCase()}` : "";
  const pfx = pathPrefixForUiLang(safeUi);
  let path = `${pfx}/${targetSeg}/${sectionSeg}${levelSuffix}`.replace(/\/+/g, "/");
  if (slug) path += `/${slug}`;
  if (sceneSlug) path += `/${sceneSlug}`;
  return path;
}

/** URL path to the German grammar hub (Grammatik) for the given UI language. */
export function getGermanGrammatikPath(uiLang) {
  return buildLocalizedPath({
    uiLang: UI_LANGS.includes(uiLang) ? uiLang : "de",
    targetCode: "de",
    sectionKey: "grammar",
  });
}

/** URL path to the German lexicon (Wortschatz) for the given UI language. */
export function getGermanVocabularyPath(uiLang) {
  return buildLocalizedPath({
    uiLang,
    targetCode: "de",
    sectionKey: "vocabulary",
  });
}

function toAbsoluteHref(origin, path) {
  if (!origin) return path;
  try {
    return new URL(path, origin).toString();
  } catch {
    return path;
  }
}

export function buildAlternateHreflangs({
  targetCode,
  sectionKey,
  slug = null,
  sceneSlug = null,
  level = null,
  slugsByUiLang = null,
  origin = null,
}) {
  if (!isSectionAvailable(targetCode, sectionKey)) return [];
  const normalizedLevel = level ? String(level).trim().toLowerCase() : null;
  /** @type {Array<{ hreflang: string, href: string }>} */
  const alternates = [];
  for (const uiLang of UI_LANGS) {
    const effectiveSlug = slugsByUiLang?.[uiLang] ?? slug;
    if (slugsByUiLang && !slugsByUiLang[uiLang]) continue;
    const path = buildLocalizedPath({
      uiLang,
      targetCode,
      sectionKey,
      level: normalizedLevel,
      slug: effectiveSlug,
      sceneSlug,
    });
    alternates.push({
      hreflang: uiLang,
      href: toAbsoluteHref(origin, path),
    });
  }
  return alternates;
}

/** `hreflang` alternates for UI-language home pages (`/`, `/en/`, `/uk/`, …). */
export function buildUiLangHomeAlternates({ origin = null } = {}) {
  return UI_LANGS.map((uiLang) => {
    const path = pathPrefixForUiLang(uiLang) || "/";
    return { hreflang: uiLang, href: toAbsoluteHref(origin, path) };
  });
}

/** `hreflang` alternates for “learn German/English/…” target landing pages. */
export function buildTargetLandingAlternates({ targetCode, origin = null }) {
  /** @type {Array<{ hreflang: string, href: string }>} */
  const alternates = [];
  for (const uiLang of UI_LANGS) {
    const seg = getTargetLandingSegment(uiLang, targetCode);
    if (!seg) continue;
    const pfx = pathPrefixForUiLang(uiLang);
    const path = `${pfx}/${seg}`.replace(/\/+/g, "/");
    alternates.push({ hreflang: uiLang, href: toAbsoluteHref(origin, path) });
  }
  return alternates;
}

/**
 * Ensure the active UI language has an `hreflang` entry pointing at the current URL
 * (pathname + search), as required for valid alternate sets.
 */
export function ensureAlternateSelfReference(alternates, uiLang, pathname, search = "", origin = null) {
  const safeUi = UI_LANGS.includes(uiLang) ? uiLang : "de";
  const path = (String(pathname || "").trim() || "/") + String(search || "");
  const selfHref = toAbsoluteHref(origin, path);
  const out = Array.isArray(alternates) ? alternates.map((alt) => ({ ...alt })) : [];
  const idx = out.findIndex((alt) => alt.hreflang === safeUi);
  if (idx >= 0) {
    out[idx] = { hreflang: safeUi, href: selfHref };
  } else {
    out.push({ hreflang: safeUi, href: selfHref });
  }
  return out;
}

/**
 * Precomputed canonical paths for the nav language switcher (no `?cid=` bootstrap).
 * @param {object} opts
 * @param {string} opts.targetCode
 * @param {string} opts.uiLang Current interface language
 * @param {string} opts.sectionKey
 * @param {string | null} [opts.slug]
 * @param {string | null} [opts.sceneSlug]
 * @param {string | null} [opts.level]
 * @param {Record<string, string> | null} [opts.slugsByUiLang] Grammar unit slugs per UI lang
 * @param {string | null} [opts.contentId]
 * @param {string | null} [opts.sceneContentId]
 */
export function buildRouteContentHint({
  targetCode,
  uiLang,
  sectionKey,
  slug = null,
  sceneSlug = null,
  level = null,
  slugsByUiLang = null,
  contentId = null,
  sceneContentId = null,
}) {
  const sec = String(sectionKey || "").trim();
  const safeUi = UI_LANGS.includes(uiLang) ? uiLang : "de";
  const normalizedLevel = level ? String(level).trim().toLowerCase() : null;

  /** @type {Record<string, string>} */
  const pathsByUiLang = {};
  for (const ui of UI_LANGS) {
    const effectiveSlug = slugsByUiLang?.[ui] ?? slug;
    if (!effectiveSlug) continue;
    if (slugsByUiLang && !slugsByUiLang[ui]) continue;
    if (!isSectionAvailable(targetCode, sec)) continue;
    pathsByUiLang[ui] = buildLocalizedPath({
      uiLang: ui,
      targetCode,
      sectionKey: sec,
      level: normalizedLevel,
      slug: effectiveSlug,
      sceneSlug,
    });
  }

  /** @type {Record<string, string>} */
  const pathsByTargetCode = {};
  const effectiveSlugForTarget = slugsByUiLang?.[safeUi] ?? slug;
  if (effectiveSlugForTarget) {
    for (const tCode of TARGET_LANGS) {
      if (!isSectionAvailable(tCode, sec)) continue;
      pathsByTargetCode[tCode] = buildLocalizedPath({
        uiLang: safeUi,
        targetCode: tCode,
        sectionKey: sec,
        level: normalizedLevel,
        slug: effectiveSlugForTarget,
        sceneSlug,
      });
    }
  }

  return {
    contentId: contentId || null,
    sceneContentId: sceneContentId || null,
    sectionKey: sec,
    pathsByUiLang,
    pathsByTargetCode,
  };
}

export function getXDefaultHref(alternates) {
  if (!Array.isArray(alternates) || alternates.length === 0) return null;
  const deAlt = alternates.find((alt) => alt.hreflang === "de");
  const enAlt = alternates.find((alt) => alt.hreflang === "en");
  return deAlt?.href || enAlt?.href || alternates[0].href || null;
}

/** `/schulen` for German UI; `?uiLang=` for all other interface languages. */
export function buildSchoolsHref(uiLang) {
  const lang = String(uiLang || "de").trim().toLowerCase() || "de";
  if (lang === "de") return "/schulen";
  return `/schulen?uiLang=${encodeURIComponent(lang)}`;
}
